/**
 * Jovii — HOW MUCH OF THE SCREEN THE KEYBOARD IS EATING.
 *
 * THE BUG THIS EXISTS FOR. The owner, on the Search tab: open it, the keyboard
 * comes up on its own, dismiss it — and the dock is left sitting too high, out
 * of line with every other page. On iOS Safari the software keyboard shrinks
 * the VISUAL viewport but not the LAYOUT viewport, and after it closes Safari
 * can leave the visual viewport scrolled inside the layout one, or restore the
 * two in separate frames. A `position: fixed` element is positioned against
 * the LAYOUT viewport, so it lands wherever that disagreement leaves it.
 *
 * THE FIX IS TO STOP GUESSING. `keyboardInset` is the number of CSS pixels the
 * visual viewport is short by, published as `--kb-inset` on <html> and added
 * to the dock's own bottom offset. At rest it is 0 and nothing changes; with a
 * keyboard up the dock rides above it; the moment it closes the value returns
 * to 0 in the same frame the viewport does, so there is no window in which the
 * dock is floating on a stale number.
 *
 * `offsetTop` is in the sum because Safari also SCROLLS the visual viewport
 * within the layout viewport, independently of the keyboard's height — and
 * that scroll is the part the owner actually saw.
 *
 * Readings are coalesced into a frame: visualViewport fires resize and scroll
 * in bursts through the keyboard animation, and writing a custom property on
 * every one of them is layout thrash for no visible gain.
 */

export function keyboardInset(view: VisualViewport | undefined, innerHeight: number): number {
  if (!view) return 0;
  /* What the layout viewport has that the visual one cannot show, less how far
     the visual viewport has been scrolled down inside it. Never negative: a
     pinch-zoomed page reports a taller visual viewport and that is not a
     keyboard. */
  const hidden = innerHeight - view.height - view.offsetTop;
  return hidden > 1 ? Math.round(hidden) : 0;
}

/*
 * AND THE PART `--kb-inset` COULD NOT FIX.
 *
 * The inset above handles the keyboard being UP. It did not fix the owner's
 * actual complaint, and measuring his screenshots said so plainly: the Search
 * dock sat 60 CSS px higher than Profile's with no keyboard on screen at all.
 *
 * The cause is narrower than "iOS and keyboards". Search is the ONLY screen
 * that is both UNSCROLLABLE (scrollHeight === innerHeight; every other screen
 * has rails running off the bottom) and AUTOFOCUSES its field. When the
 * keyboard opens on an autofocused input, WebKit scrolls the input into view -
 * and with no document scroll available it shifts the WEB VIEW itself instead.
 * That shift is not `window.scrollY`, not `visualViewport.offsetTop`, and it
 * is not undone when the keyboard closes, which is why a fixed element came
 * back 60px high and why nothing in the viewport numbers ever looked wrong.
 *
 * `window.scrollTo(0, 0)` is what puts a shifted web view back, and it is safe
 * here precisely because it only runs when the page has no scroll of its own.
 */
function settleWebView(): void {
  if (document.documentElement.scrollHeight > window.innerHeight + 1) return;
  window.scrollTo(0, 0);
}

/** Publish `--kb-inset` on <html> for as long as this is installed. */
export function trackKeyboardInset(): () => void {
  if (typeof window === 'undefined') return () => {};
  const view = window.visualViewport;
  const root = document.documentElement;
  if (!view) {
    root.style.setProperty('--kb-inset', '0px');
    return () => root.style.removeProperty('--kb-inset');
  }

  let frame = 0;
  let last = -1;
  const flush = (): void => {
    frame = 0;
    const next = keyboardInset(view, window.innerHeight);
    if (next === last) return;
    const closing = last > 0 && next === 0;
    last = next;
    root.style.setProperty('--kb-inset', `${next}px`);
    /* The keyboard just left. Put back any web-view shift it caused. */
    if (closing) window.requestAnimationFrame(settleWebView);
  };
  const update = (): void => {
    if (frame) return;
    frame = window.requestAnimationFrame(flush);
  };

  update();
  view.addEventListener('resize', update);
  view.addEventListener('scroll', update);
  window.addEventListener('orientationchange', update);

  return () => {
    if (frame) window.cancelAnimationFrame(frame);
    view.removeEventListener('resize', update);
    view.removeEventListener('scroll', update);
    window.removeEventListener('orientationchange', update);
    root.style.removeProperty('--kb-inset');
  };
}
